import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { _WM_APP_PROJECT } from '../utils/utils';
import { runtimeEventManager } from "./event-manager";

interface Scripts {
  name: string;
  src: string;
}

export const ScriptStore: Scripts[] = [];

declare var document: any;

@Injectable({ providedIn: 'root' })
export class ScriptLoaderService {

  private scripts: any = {};
  private pathMappings: any = {};
  private lazyScripts: any = {};
  private environment: any;

  constructor(private http: HttpClient) {
    ScriptStore.forEach((script: any) => {
      this.scripts[script.name] = {
        loaded: false,
        src: script.src
      };
    });
    this.environment = runtimeEventManager.getService('environment');
  }

  setEnvironment() {
    this.environment = runtimeEventManager.getService('environment');
  }

  private isScriptLoaded(src: string) {
    return !!document.querySelector('script[src="' + src + '"]');
  }

  loadLazyScript(name: string): Promise<{ script: string, loaded: boolean, error?: any }> {
    return new Promise((resolve, reject) => {
      if (!this.environment) {
        this.setEnvironment();
      }
      if (this.lazyScripts[name] && this.lazyScripts[name].loaded) {
        //resolve promise immediately if script already loaded
        resolve({ script: name, loaded: true });
        return;
      } else if (!this.lazyScripts[name]) {
        //if script is not loaded and not in progress then load the script and resolve promise in script onload callback
        const buildNumber = this.environment?.BUILD_NUMBER || '';
        const cdnBaseURL = this.environment?.CDN_BASE_URL || '';
        const _url = cdnBaseURL ? cdnBaseURL : buildNumber ? buildNumber + '/' : '';
        const src = _url ? `${_url}${name}.js` : `${name}.js`;
        this.lazyScripts[name] = {
          loaded: false,
          src: src,
          waiting: []
        };
      } else if (this.lazyScripts[name]) {
        //if script loading is already in progress then push the resolve to waiting list and wait for the existing script to load and resolve all waiting resolves in existing script onload callback
        this.lazyScripts[name].waiting.push(resolve);
      }

      let script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = this.lazyScripts[name].src;

      script.onload = () => {
        this.lazyScripts[name].loaded = true;
        resolve({ script: name, loaded: true });
        this.lazyScripts[name].waiting.forEach((resolve: any) => resolve({ script: name, loaded: true }));
        this.lazyScripts[name].waiting = [];
      };

      script.onerror = (error: any) => resolve({ script: name, loaded: false, error: error });
      if (!this.isScriptLoaded(this.lazyScripts[name].src)) {
        document.getElementsByTagName('head')[0].appendChild(script);
      }
    });
  }

  load(...scripts: string[]): Promise<any> {
    if (scripts && scripts.length) {
      return Promise.resolve()
        .then(() => this.pathMappings || this.loadPathMappings())
        .then(() => {
          return Promise.all(scripts.map(s => {
            return this.loadScript(s);
          }));
        });
    }
    return Promise.resolve();
  }

  private loadPathMappings() {
    const path = (_WM_APP_PROJECT.cdnUrl || _WM_APP_PROJECT.ngDest) + 'path_mapping.json';
    return this.http.get(path).toPromise().then((data) => {
      this.pathMappings = data;
    }, () => {
      this.pathMappings = {};
    })
  }

  private loadScript(name: string) {
    return new Promise((resolve, reject) => {
      if (this.scripts[name] && this.scripts[name].loaded) {
        resolve({ script: name, loaded: true });
        return;
      } else if (!this.scripts[name]) {
        this.scripts[name] = {
          loaded: false,
          src: name
        };
      }
      //load script
      let script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = false;
      let src = this.scripts[name].src;
      src = this.pathMappings[src] || src;
      if (src.startsWith("http")) {
        script.src = src;
      } else {
        script.src = (_WM_APP_PROJECT.cdnUrl || _WM_APP_PROJECT.ngDest) + src;
      }
      if (script.readyState) {  //IE
        script.onreadystatechange = () => {
          if (script.readyState === "loaded" || script.readyState === "complete") {
            script.onreadystatechange = null;
            this.scripts[name].loaded = true;
            resolve({ script: name, loaded: true });
          }
        };
      } else {  //Others
        script.onload = () => {
          this.scripts[name].loaded = true;
          resolve({ script: name, loaded: true });
        };
      }
      script.onerror = (error: any) => resolve({ script: name, loaded: false, status: 'Loaded' });
      document.getElementsByTagName('head')[0].appendChild(script);
    });
  }

  isRequireScriptLoaded(baseURL: string) {
    const scripts = document.getElementsByTagName('script')
    for (let i = 0; i < scripts.length; i++) {
      if (scripts[i].src.includes(`${baseURL}/requirejs/require.js`)) {
        return true;
      }
    }
    return false
  }

  createLoaderScriptElement(baseURL: string): HTMLScriptElement {
    const loaderScript: HTMLScriptElement = document.createElement('script');
    loaderScript.src = `${baseURL}/requirejs/require.js`
    return loaderScript;
  }

}
